import React, { useEffect, useState } from 'react';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import { Button, Col, FormControl, InputGroup, Row, Spinner } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { usePageTitle } from '../../../../../../App';
import avatar from '../../../../../../assets/images/no-image-found.jpg';
import { IMAGE_URL } from '../../../../../../constants/Config';
import {
  FIRST_NAME_ALPHABET,
  FIRST_NAME_MAX_VALID,
  FIRST_NAME_REQURIED,
  LAST_NAME_ALPHABET,
  LAST_NAME_MAX_VALID,
  LAST_NAME_REQURIED,
  PAYPAL_EMAIL_ID_MAX_VALID,
  PAYPAL_EMAIL_ID_VALID,
  PHONE_NUMBER_MAX_REQURIED,
  PHONE_NUMBER_REQURIED,
  PHONE_NUMBER_VALID,
  UPLOAD_IMAGE_FILE_VALID,
  UPLOAD_IMAGE_SIZE_VALID,
  ZIP_CODE_VALID,
} from '../../../../../../constants/ValidationMessageConstants';
import { getUserInfo } from '../../../../../../utils/UserInfoUtils';
import { profileEditRequest, profileRequest, stateListRequest } from '../actions';
import { trackEvent } from '../../../../../../utils/ga';

const Profile = () => {
  usePageTitle('Datagain Transcription  - Profile > Personal Information'); // Set tab title

  const userInfo = getUserInfo();
  const dispatch = useDispatch();

  const [selectedCountry, setSelectedCountry] = useState(0);

  const [profileImage, setProfileImage] = useState(
    userInfo?.ProfilePicture !== ('' || null) ? `${IMAGE_URL}/${userInfo.ProfilePicture}` : null,
  );

  useEffect(() => {
    dispatch(profileRequest(userInfo?.UserId));
  }, [dispatch, userInfo?.UserId]);

  // Get user profile details
  let userProfileDetails = useSelector((state) => state?.userProfileDetails?.user?.data?.value);

  let profileData = userProfileDetails && JSON.parse(userProfileDetails);

  let userProfileDetailsLoading = useSelector((state) => state?.userProfileDetails?.loading);

  useEffect(() => {
    if (profileData?.Country && profileData?.statelist) {
      const countryId = selectedCountry !== 0 ? selectedCountry : profileData.Country;
      setSelectedCountry(countryId);
    } else {
      setSelectedCountry(0);
    }
  }, [profileData, selectedCountry]);

  useEffect(() => {
    if (selectedCountry !== 0) {
      dispatch(stateListRequest(selectedCountry));
    }
  }, [dispatch, selectedCountry]);

  // Get countrywise state list
  let userStateList = useSelector((state) => state?.stateList?.user?.data?.value);

  let stateList = userStateList && JSON.parse(userStateList);

  // Form's initialValues
  const initialValues = {
    LastName: profileData?.LastName || '',
    Zip: profileData?.Zip || '',
    Level: profileData?.Level,
    Specilization: profileData?.Specilization || '',
    UserId: userInfo?.UserId,
    PaypalEmail: profileData?.PaypalEmail || '',
    City: profileData?.City || '',
    State: profileData?.State,
    ProfilePicture: null,
    Country: selectedCountry !== 0 ? selectedCountry : profileData?.Country,
    PhoneNumber: profileData?.PhoneNumber || '',
    FirstName: profileData?.FirstName || '',
  };

  // Form's validationSchema
  const validationSchema = Yup.object({
    FirstName: Yup.string()
      .matches(/^[A-Za-z\s]*$/, FIRST_NAME_ALPHABET)
      .max(255, FIRST_NAME_MAX_VALID)
      .required(FIRST_NAME_REQURIED),
    LastName: Yup.string()
      .matches(/^[A-Za-z\s]*$/, LAST_NAME_ALPHABET)
      .max(255, LAST_NAME_MAX_VALID)
      .required(LAST_NAME_REQURIED),
    PhoneNumber: Yup.string()
      .required(PHONE_NUMBER_REQURIED)
      .matches(/^\d{10}$/, PHONE_NUMBER_MAX_REQURIED)
      .matches(/^[\d\s()-]+$/, PHONE_NUMBER_VALID),
    Zip: Yup.string().matches(/^\d+$/, ZIP_CODE_VALID),
    PaypalEmail: Yup.string()
      .email(PAYPAL_EMAIL_ID_VALID)
      .max(255, PAYPAL_EMAIL_ID_MAX_VALID)
      .matches(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, PAYPAL_EMAIL_ID_VALID),
    ProfilePicture: Yup.mixed()
      .nullable()
      .test('fileSize', UPLOAD_IMAGE_SIZE_VALID, (file) => !file || (file && file.size <= 10 * 1024 * 1024))
      .test(
        'fileType',
        UPLOAD_IMAGE_FILE_VALID,
        (file) => !file || (file && ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'].includes(file.type)),
      ),
  });

  // Edit profile data for use this function
  const onSubmit = async (values, { setSubmitting }) => {
    const formData = new FormData();
    if (profileImage && profileImage.includes('blob:') === false) {
      values.isImgUploaded = false;
    } else {
      values.isImgUploaded = true;
    }

    for (const key in values) {
      if (key === 'ProfilePicture' && values.ProfilePicture instanceof File) {
        formData.append(key, values[key]);
      } else {
        formData.append(key, values[key]);
      }
    }

    try {
      dispatch(profileEditRequest(formData));
      trackEvent('proofreader_profile_update');
      setTimeout(() => {
        dispatch(profileRequest(userInfo?.UserId));
      }, 800);
      return () => clearTimeout(800);
    } catch (error) {
      toast.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleProfileImageChange = (event, setFieldValue) => {
    const file = event.currentTarget.files[0];
    setFieldValue('ProfilePicture', file);
    setProfileImage(URL.createObjectURL(file));
  };

  return (
    <div className="profile-wrapper">
      <Row>
        {userProfileDetailsLoading === false ? (
          <Col md="8">
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={onSubmit}
              validateOnBlur={false}
              validateOnChange={false}
            >
              {({ isSubmitting, setFieldValue }) => (
                <Form>
                  <Row>
                    <Col md="12">
                      <div className="fw-bold fnt-18 mb-4">Profile</div>
                    </Col>
                    <Col xs="12" className="mb-4">
                      <div className="preview">
                        <img id="img-preview" src={profileImage || avatar} alt="Profile Preview" />
                        <div className="profile-upload-wrapper">
                          <div className="fw-bold">Profile Picture</div>
                          <div className="d-flex align-items-center position-relative">
                            <Button className="file-input-label">
                              <input
                                accept=".png, .jpg, .jpeg, .gif"
                                className="fileInputHidden"
                                type="file"
                                id="file-input"
                                onChange={(event) => handleProfileImageChange(event, setFieldValue)}
                                autoComplete="off"
                              />
                              <FileUploadOutlinedIcon /> Upload Image
                            </Button>
                            <button
                              type="button"
                              className="profile-remove-btn"
                              disabled={!profileImage}
                              onClick={() => {
                                setFieldValue('ProfilePicture', null);
                                setProfileImage(null);
                              }}
                            >
                              <DeleteOutlineOutlinedIcon /> Remove
                            </button>
                          </div>
                          <p className="mb-0 upload-info-label">We support PNGs, JPEGs, and GIFs under 10MB</p>
                        </div>
                      </div>
                      <ErrorMessage name="ProfilePicture" component="div" className="invalid-feedback" />
                    </Col>
                    <Col md="6">
                      <div className="mb-3 position-relative">
                        <label htmlFor="FirstName" className="form-label">
                          First Name
                        </label>
                        <Field
                          type="text"
                          id="FirstName"
                          name="FirstName"
                          className="form-control"
                          placeholder="Enter first name"
                          autoComplete="off"
                        />
                        <span className="field-mandatory">*</span>
                        <ErrorMessage name="FirstName" component="div" className="invalid-feedback" />
                      </div>
                    </Col>
                    <Col md="6">
                      <div className="mb-3 position-relative">
                        <label htmlFor="LastName" className="form-label">
                          Last Name
                        </label>
                        <Field
                          type="text"
                          id="LastName"
                          name="LastName"
                          className="form-control"
                          placeholder="Enter last name"
                          autoComplete="off"
                        />
                        <span className="field-mandatory">*</span>
                        <ErrorMessage name="LastName" component="div" className="invalid-feedback" />
                      </div>
                    </Col>
                    <Col md="6">
                      <div className="mb-3">
                        <label htmlFor="Email" className="form-label">
                          Email
                        </label>
                        <InputGroup>
                          <Field
                            as={FormControl}
                            type="email"
                            id="Email"
                            name="Email"
                            value={profileData?.Email}
                            placeholder="Enter email address"
                            autoComplete="nope"
                            data-lpignore="true"
                            disabled
                          />
                          <InputGroup.Text>
                            <LockOutlinedIcon />
                          </InputGroup.Text>
                        </InputGroup>
                      </div>
                    </Col>
                    <Col md="6">
                      <div className="mb-3">
                        <label htmlFor="PhoneNumber" className="form-label">
                          Phone Number
                        </label>
                        <Field
                          type="number"
                          id="PhoneNumber"
                          name="PhoneNumber"
                          className="form-control"
                          placeholder="Enter phone number"
                          autoComplete="off"
                          onKeyDown={(e) => {
                            if (
                              e.key === 'e' ||
                              e.key === 'E' ||
                              e.key === '+' ||
                              e.key === '-' ||
                              (e.key === '0' && e.target.value.length === 0)
                            ) {
                              e.preventDefault();
                            }
                          }}
                        />
                        <ErrorMessage name="PhoneNumber" component="div" className="invalid-feedback" />
                      </div>
                    </Col>
                    <Col md="6">
                      <div className="mb-3">
                        <label htmlFor="Country" className="form-label">
                          Country
                        </label>
                        <Field
                          type="text"
                          id="Country"
                          name="Country"
                          className="form-control"
                          as="select"
                          disabled
                          onChange={(e) => {
                            const countryId = e.target.value;
                            setSelectedCountry(countryId);
                            setFieldValue('Country', countryId);
                          }}
                          value={selectedCountry}
                          autoComplete="off"
                        >
                          <option value={0} disabled>
                            Select Country
                          </option>
                          {profileData?.countrylist &&
                            profileData?.countrylist.map((data, index) => (
                              <option key={index} value={data.Id}>
                                {data.Name}
                              </option>
                            ))}
                        </Field>
                        <ErrorMessage name="Country" component="div" className="invalid-feedback" />
                      </div>
                    </Col>
                    <Col md="6">
                      <div className="mb-3">
                        <label htmlFor="State" className="form-label">
                          State
                        </label>
                        <Field
                          type="text"
                          id="State"
                          name="State"
                          className="form-control"
                          as="select"
                          disabled
                          autoComplete="off"
                        >
                          <option value={0} disabled>
                            Select State
                          </option>
                          {stateList &&
                            stateList.map((data, index) => (
                              <option key={index} value={data.Id}>
                                {data.Name}
                              </option>
                            ))}
                        </Field>
                        <ErrorMessage name="State" component="div" className="invalid-feedback" />
                      </div>
                    </Col>
                    <Col md="6">
                      <div className="mb-3">
                        <label htmlFor="City" className="form-label">
                          City
                        </label>
                        <Field
                          type="text"
                          id="City"
                          name="City"
                          className="form-control"
                          placeholder="Enter city"
                          autoComplete="off"
                          disabled
                        />
                        <ErrorMessage name="City" component="div" className="invalid-feedback" />
                      </div>
                    </Col>
                    <Col md="6">
                      <div className="mb-3">
                        <label htmlFor="Zip" className="form-label">
                          Zip
                        </label>
                        <Field
                          type="text"
                          id="Zip"
                          name="Zip"
                          className="form-control"
                          placeholder="Enter zip code"
                          autoComplete="off"
                        />
                        <ErrorMessage name="Zip" component="div" className="invalid-feedback" />
                      </div>
                    </Col>
                    <Col md="6">
                      <div className="mb-3">
                        <label htmlFor="Specilization" className="form-label">
                          Expertise
                        </label>
                        <Field
                          type="text"
                          id="Specilization"
                          name="Specilization"
                          className="form-control"
                          as="select"
                          autoComplete="off"
                        >
                          <option value="">Select Expertise</option>
                          {profileData?.expertiselist &&
                            profileData.expertiselist.map((data, index) => (
                              <option key={index} value={data.Id}>
                                {data.Name}
                              </option>
                            ))}
                        </Field>
                        <ErrorMessage name="Specilization" component="div" className="invalid-feedback" />
                      </div>
                    </Col>
                    <Col md="6">
                      <div className="mb-3">
                        <label htmlFor="PaypalEmail" className="form-label">
                          PayPal Email
                        </label>
                        <Field
                          type="email"
                          id="PaypalEmail"
                          name="PaypalEmail"
                          className="form-control"
                          placeholder="Enter paypal email address"
                          autoComplete="nope"
                          data-lpignore="true"
                        />
                        <ErrorMessage name="PaypalEmail" component="div" className="invalid-feedback" />
                      </div>
                    </Col>
                    <Col md="6">
                      <div className="mb-3 position-relative">
                        <label htmlFor="Level" className="form-label">
                          Level
                        </label>
                        <Field
                          type="text"
                          id="Level"
                          name="Level"
                          className="form-control"
                          placeholder="Enter level"
                          autoComplete="off"
                          disabled
                        />
                      </div>
                    </Col>
                    <Col md="6"></Col>
                    <Col xs="12" className="mt-4">
                      <Button type="submit" variant="primary" disabled={isSubmitting}>
                        Save Changes
                      </Button>
                    </Col>
                  </Row>
                </Form>
              )}
            </Formik>
          </Col>
        ) : (
          <div
            style={{
              position: 'absolute',
              width: 'calc(100% - 330px)',
              height: 'calc(100% - 580px)',
              zIndex: 1,
              display: 'flex',
              justifyContent: 'center',
              marginTop: '30vh',
            }}
          >
            <Spinner animation="border" />
          </div>
        )}
      </Row>
    </div>
  );
};

export default Profile;
